import { apiFetch, apiFetchV2 } from '@m/utils/http';

import type { ReservationFeeDetail, ReservationFeePlan } from '@m/types/api';

const RESERVATION_FEES_PREFIX = '/admin/reservation-fees';

type ReservationFeePlanResponse = ReservationFeeDetail & {
  fee?: ReservationFeeDetail;
  siteId?: number;
};

function normalizeReservationFeeDetail(plan: ReservationFeePlanResponse): ReservationFeeDetail {
  return plan.fee ?? plan;
}

function normalizeReservationFeePlans(plans: ReservationFeePlanResponse[]): ReservationFeeDetail[] {
  return plans.map(normalizeReservationFeeDetail);
}

function normalizeReservationFeesForZone(
  plans: ReservationFeePlanResponse[],
): ReservationFeePlan[] {
  return plans.map((plan) => {
    const fee = normalizeReservationFeeDetail(plan);
    return {
      fee,
      id: plan.id ?? fee.id,
      siteId: plan.siteId ?? 0,
    };
  });
}

export const FeeService = {
  getTransactionFeePlans: () => apiFetch('/admin/transaction-fees'),
  getTransactionFeePlansV2: () => apiFetchV2('/admin/transaction-fees'),

  getTransactionFeesForSite: ({ siteId }: { siteId: number }) =>
    apiFetch(`/admin/transaction-fees/fees-for-site?siteId=${siteId}`),
  getTransactionFeesForSiteV2: ({ siteId }: { siteId: number }) =>
    apiFetchV2(`/admin/transaction-fees/fees-for-site?siteId=${siteId}`),

  enableTransactionFeeForSite: ({ siteId, feeId }: { feeId: number; siteId: number }) =>
    apiFetch(`/admin/transaction-fees/${feeId}?siteId=${siteId}`, { method: 'POST' }),
  enableTransactionFeeForSiteV2: ({ siteId, feeId }: { feeId: number; siteId: number }) =>
    apiFetchV2(`/admin/transaction-fees/${feeId}?siteId=${siteId}`, { method: 'POST' }),

  disableTransactionFeeForSite: ({ siteFeeId }: { siteFeeId: number }) =>
    apiFetch(`/admin/transaction-fees/remove-fee-from-site?siteFeeId=${siteFeeId}`, {
      method: 'DELETE',
    }),
  disableTransactionFeeForSiteV2: ({ siteFeeId }: { siteFeeId: number }) =>
    apiFetchV2(`/admin/transaction-fees/remove-fee-from-site?siteFeeId=${siteFeeId}`, {
      method: 'DELETE',
    }),

  assignTransactionFeeForSite: ({
    siteId,
    fees,
  }: {
    fees: { transactionFeePlanId: number; untilMinutesElapsed?: number | null }[];
    siteId: number;
  }) =>
    apiFetch(`/admin/transaction-fees/update-zone-fee`, {
      method: 'POST',
      body: fees.map((data) => ({ ...data, siteId })),
      params: { siteId },
    }),
  assignTransactionFeeForSiteV2: ({
    siteId,
    fees,
  }: {
    fees: { transactionFeePlanId: number; untilMinutesElapsed?: number | null }[];
    siteId: number;
  }) =>
    apiFetchV2(`/admin/transaction-fees/update-zone-fee`, {
      method: 'POST',
      body: fees.map((data) => ({ ...data, siteId })),
      params: { siteId },
    }),

  getSubscriptionFeePlans: () => apiFetch('/admin/subscription-fees'),
  getSubscriptionFeePlansV2: () => apiFetchV2('/admin/subscription-fees'),

  getSubscriptionFeesForSite: ({ siteId }: { siteId: number }) =>
    apiFetch(`/admin/subscription-fees/fees-for-site?siteId=${siteId}`),
  getSubscriptionFeesForSiteV2: ({ siteId }: { siteId: number }) =>
    apiFetchV2(`/admin/subscription-fees/fees-for-site?siteId=${siteId}`),

  enableSubscriptionFeeForSite: ({ siteId, feeId }: { feeId: number; siteId: number }) =>
    apiFetch(`/admin/subscription-fees/${feeId}?siteId=${siteId}`, { method: 'POST' }),
  enableSubscriptionFeeForSiteV2: ({ siteId, feeId }: { feeId: number; siteId: number }) =>
    apiFetchV2(`/admin/subscription-fees/${feeId}?siteId=${siteId}`, { method: 'POST' }),

  disableSubscriptionFeeForSite: ({ siteFeeId }: { siteFeeId: number }) =>
    apiFetch(`/admin/subscription-fees/remove-fee-from-site?siteFeeId=${siteFeeId}`, {
      method: 'DELETE',
    }),
  disableSubscriptionFeeForSiteV2: ({ siteFeeId }: { siteFeeId: number }) =>
    apiFetchV2(`/admin/subscription-fees/remove-fee-from-site?siteFeeId=${siteFeeId}`, {
      method: 'DELETE',
    }),

  createSubscriptionFeePlan: (body: {
    flatAmount?: number;
    maximum: number;
    minimum: number;
    percent?: number;
  }) =>
    apiFetch('/admin/subscription-fees', {
      method: 'POST',
      body,
    }),
  createSubscriptionFeePlanV2: (body: {
    flatAmount?: number;
    maximum: number;
    minimum: number;
    percent?: number;
  }) =>
    apiFetchV2('/admin/subscription-fees', {
      method: 'POST',
      body,
    }),

  getSubscriptionFeeOverrideConfig: ({ subscriptionId }: { subscriptionId: number }) =>
    apiFetch(`/v2/subscriptions/${subscriptionId}/fees/override`, {
      method: 'GET',
    }),

  updateSubscriptionFeeOverrideConfig: ({
    subscriptionId,
    enabled,
  }: {
    enabled: boolean;
    subscriptionId: number;
  }) =>
    apiFetch(`/v2/subscriptions/${subscriptionId}/fees/override`, {
      method: 'POST',
      body: { enabled },
    }),

  createTransactionFeePlan: (body: { maximum: number; minimum: number; percent?: number }) =>
    apiFetch('/admin/transaction-fees', {
      method: 'POST',
      body,
    }),
  createTransactionFeePlanV2: (body: { maximum: number; minimum: number; percent?: number }) =>
    apiFetchV2('/admin/transaction-fees', {
      method: 'POST',
      body,
    }),

  getReservationFeePlans: async () => {
    const plans = await apiFetchV2<ReservationFeePlanResponse[]>(
      `${RESERVATION_FEES_PREFIX}/plans`,
    );
    return normalizeReservationFeePlans(plans);
  },

  getReservationFeePlan: async ({ planId }: { planId: number }) => {
    const plan = await apiFetchV2<ReservationFeePlanResponse>(
      `${RESERVATION_FEES_PREFIX}/plans/${planId}`,
    );
    return normalizeReservationFeeDetail(plan);
  },

  getReservationFeesForZone: async ({ zoneId }: { zoneId: number }) => {
    const plans = await apiFetchV2<ReservationFeePlanResponse[]>(
      `${RESERVATION_FEES_PREFIX}/zones/${zoneId}/plans`,
    );
    return normalizeReservationFeesForZone(plans);
  },

  enableReservationFeeForZone: ({ zoneId, feeId }: { feeId: number; zoneId: number }) =>
    apiFetchV2(`${RESERVATION_FEES_PREFIX}/zones/${zoneId}/plans/${feeId}`, { method: 'POST' }),

  disableReservationFeeForZone: ({ zoneId, feeId }: { feeId: number; zoneId: number }) =>
    apiFetchV2(`${RESERVATION_FEES_PREFIX}/zones/${zoneId}/plans/${feeId}`, {
      method: 'DELETE',
    }),

  createReservationFeePlan: (body: {
    flatAmount?: number;
    maximum: number;
    minimum: number;
    notes?: string;
    percent?: number;
  }) =>
    apiFetchV2(`${RESERVATION_FEES_PREFIX}/plans`, {
      method: 'POST',
      body,
    }),
};
